"use client";

import { OffersOffer } from '@/services/dataTypes';
import React, { useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import Banner from './Banner';

interface Props {
    bannerResponse: OffersOffer[];
    domain: string;
    mer_slug: string;
    slug_type: string;
}

const VerticalEventOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type }: Props) => {
    const filtered = filterOfferBanners(bannerResponse, 50, 2000, 65, 2000);
    const [visibleCount, setVisibleCount] = useState(20);
    const [loading, setLoading] = useState(false);

    const handleShowMore = () => {
        if (loading) return;
        setLoading(true);
        setTimeout(() => {
            setVisibleCount(prev => Math.min(prev + 6, filtered?.length));
            setLoading(false);
        }, 500);
    };

    const handleShowLess = () => {
        setVisibleCount(20);
    };

    const hasMore = visibleCount < filtered.length;
    const isExpanded = visibleCount > 20;

    if (filtered.length === 0) return null;

    return (
        <div className="w-full bg-white/80 backdrop-blur-xl border border-slate-100 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.02)] overflow-hidden">
          
            {/* --- 2. BANNER LIST AREA --- */}
            <div className="flex flex-col gap-5 p-5">
                {filtered.slice(0, visibleCount).map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data);
                    return (
                        <div 
                            key={i} 
                            className="relative group rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-slate-200"
                        >
                            {/* Overlay Glow on Hover */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                            
                            <div className="transition-transform duration-500 group-hover:scale-[1.03]">
                                <Banner
                                    data={offer_data}
                                    height={dimension?.height}
                                    mer_slug={mer_slug}
                                    slug_type={slug_type}
                                    domain={domain}
                                    width={dimension?.width}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- 3. ULTRA MODERN CONTROLS --- */}
            {(hasMore || isExpanded) && (
                <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col items-center gap-4">
                    {hasMore && (
                        <button
                            onClick={handleShowMore}
                            disabled={loading}
                            className="group relative w-full overflow-hidden py-3.5 px-6 bg-slate-900 rounded-2xl transition-all duration-300 hover:bg-[#D61C4E] hover:shadow-[0_10px_30px_rgba(214,28,78,0.3)] disabled:opacity-50"
                        >
                            {/* Shimmer Effect */}
                            <div className="absolute inset-0 w-1/2 h-full bg-white/10 -skew-x-[20deg] translate-x-[-150%] transition-transform duration-1000 group-hover:translate-x-[250%]" />
                            
                            <div className="relative flex items-center justify-center gap-3">
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <span className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
                                        Expand Inventory
                                    </span>
                                )}
                            </div>
                        </button>
                    )}

                    {isExpanded && !loading && (
                        <button
                            onClick={handleShowLess}
                            className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-[#D61C4E] transition-colors"
                        >
                            Collapse All
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default VerticalEventOfferBanner;